import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, TextField } from '@/ui';
import { login, setPermissions, setToken, type LoginData } from '@/services/auth';
import { errorMessage } from '@/lib/query';
import { track } from '@/lib/analytics';
import { ApiError } from '@/services/api';

/**
 * Sign in.
 *
 * The one screen in the app that is not a register, so it is the one screen
 * that gets to be composed rather than ruled: the form sits on canvas, centred,
 * No illustration, no
 * gradient, no marketing copy — this is a tool that staff open at 8am, and the
 * fastest sign-in is one with nothing to look at.
 */
export function LoginScreen({ onSignedIn }: { onSignedIn: (data: LoginData) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const signIn = useMutation({
    mutationFn: () => login(email, password),
    onSuccess: (data) => {
      setToken(data.token);
      setPermissions(data.permissions);
      onSignedIn(data);
    },
    onError: (error) => {
      /* The other half of `Signed In`. A wrong password and a server that is
         refusing everyone look identical from a support call; `status` is what
         separates them, and 0 says the request never left the building. */
      track('Sign In Failed', { status: error instanceof ApiError ? error.status : 0 });
    },
  });

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-canvas px-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          signIn.mutate();
        }}
        className="w-full max-w-xs"
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold tracking-wide text-ink">GURUKUL</h1>
          <p className="mt-0.5 text-xs text-muted">School management</p>
        </div>

        <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4">
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={signIn.isError ? errorMessage(signIn.error) : undefined}
          />
          <Button
            type="submit"
            variant="primary"
            loading={signIn.isPending}
            className="mt-1 h-9 w-full"
          >
            Sign in
          </Button>
        </div>
      </form>
    </div>
  );
}
